import * as THREE from 'three';
import { Scene } from './Scene';
import { Astronaut } from '../gameplay/astronaut';
import type { InputManager } from '../input';
import type { AudioManager } from '../audio';
import type { FactionSelection } from '../gameplay/factions';
import type { PlayerStats } from '../gameplay/player';
import { getFaction, FACTIONS } from '../gameplay/factions';
import type { SurfaceObstacle } from '../gameplay/surface';
import type { Terrain } from '../gameplay/surface';
import { PlayerShip } from '../gameplay/ships';
import { NpcShip } from '../gameplay/npcs';
import {
  buildAlcove,
  buildHangar,
  buildStationInterior,
  SHOP_ORDER,
  StationCounter,
  StationVendor,
  WanderingNpc,
  getShopDefinition,
  StationLayout
} from '../gameplay/station';
import type { System } from '../systems';
import { StationExitPortal } from '../gameplay/portals';

export interface StationInteriorSceneOptions {
  readonly input: InputManager;
  readonly camera: THREE.Camera;
  readonly audio: AudioManager;
  readonly faction: FactionSelection;
  readonly stats: PlayerStats;
}

export class StationInteriorScene extends Scene {
  readonly id = 'station-interior';

  private astronaut: Astronaut | null = null;
  private readonly counters: StationCounter[] = [];
  private readonly vendors: StationVendor[] = [];
  private readonly wanderers: WanderingNpc[] = [];
  private readonly _obstacles: SurfaceObstacle[] = [];
  private readonly spawnPosition = new THREE.Vector3(
    StationLayout.PLAYER_SPAWN_X,
    StationLayout.FLOOR_Y,
    StationLayout.PLAYER_SPAWN_Z
  );
  private readonly spawnFacing = StationLayout.PLAYER_SPAWN_FACING;
  private readonly flatTerrain: Terrain;
  private exitPosition = new THREE.Vector3();
  private exitRadius = StationLayout.EXIT_PAD_RADIUS;
  private loaded = false;
  private vibejamPortal: StationExitPortal | null = null;

  constructor(private readonly options: StationInteriorSceneOptions) {
    super();
    this.flatTerrain = {
      getHeightAt: () => StationLayout.FLOOR_Y,
      // Mirror getHeightAt — the station floor is flat and never has a
      // water layer, so the collision-aware getter is the same value.
      // Required because FirstPersonController calls this method on
      // every tick.
      getCollisionGroundY: () => StationLayout.FLOOR_Y
    } as unknown as Terrain;
  }

  override async load(): Promise<void> {
    if (this.loaded) return;
    this.loaded = true;
    this.setupAtmosphere();
    this.setupLighting();
    this.buildArchitecture();
    this.buildAlcoves();
    this.buildHangarZone();
    this.spawnWanderers();
    this.spawnAstronaut();
    this.spawnVibejamPortal();
    this.registerUpdateSystem();
    await super.load();
  }

  /** Drop the Vibejam exit portal in a quiet corner of the atrium —
   *  back wall (angle = π) at a comfortable radius, facing the
   *  centre so the player walks straight into it. The arch itself is
   *  intentionally NOT registered as an obstacle — the player has to
   *  walk through it for the hand-off to fire. The sign on the right
   *  side gets its own thin obstacle so a player can't clip through
   *  the pole. */
  private spawnVibejamPortal(): void {
    const radius = StationLayout.INTERIOR_RADIUS * 0.78;
    const x = Math.sin(Math.PI) * radius;
    const z = Math.cos(Math.PI) * radius;
    const center = new THREE.Vector3(x, StationLayout.FLOOR_Y, z);
    this.vibejamPortal = new StationExitPortal({
      position: center,
      // Sign + arch face the atrium centre (player approaches from
      // +Z towards -Z, so the local +Z forward needs to point toward
      // world +Z — facing of 0 keeps that natural).
      facing: 0
    });
    this.root.add(this.vibejamPortal.object3D);
    // Sign-pole obstacle only. The arch opening must remain walkable.
    // Coordinates mirror the SIDE_X / SIDE_Z constants in
    // StationExitPortal.buildPlinthAndSign — keep them in sync.
    this._obstacles.push({
      position: new THREE.Vector3(center.x + 2.65, 0, center.z + 0.65),
      radius: 0.5
    });
  }

  getVibejamPortal(): StationExitPortal | null {
    return this.vibejamPortal;
  }

  get terrainInstance(): Terrain {
    return this.flatTerrain;
  }

  get obstacles(): readonly SurfaceObstacle[] {
    return this._obstacles;
  }

  get astronautEntity(): Astronaut {
    if (!this.astronaut) throw new Error('Astronaut not initialized');
    return this.astronaut;
  }

  getCounters(): readonly StationCounter[] {
    return this.counters;
  }

  getExitPosition(): THREE.Vector3 {
    return this.exitPosition.clone();
  }

  getExitRadius(): number {
    return this.exitRadius;
  }

  getSpawnPosition(): THREE.Vector3 {
    return this.spawnPosition.clone();
  }

  getSpawnFacing(): number {
    return this.spawnFacing;
  }

  placeAstronautAtSpawn(): void {
    if (!this.astronaut) return;
    this.astronaut.object3D.position.copy(this.spawnPosition);
    this.astronaut.object3D.rotation.set(0, this.spawnFacing, 0);
    this.astronaut.velocity.set(0, 0, 0);
    this.astronaut.isGrounded = true;
  }

  // -------------------------------------------------------------------
  private setupAtmosphere(): void {
    this.root.background = new THREE.Color(0x04070f);
    this.root.fog = new THREE.Fog(0x0a1024, 60, 180);
  }

  private setupLighting(): void {
    // Warm + cool dual lighting for that "Nexus" mood.
    const ambient = new THREE.AmbientLight(0x7a9bd6, 0.48);
    this.root.add(ambient);

    const hemi = new THREE.HemisphereLight(0xb4d7ff, 0x20253a, 0.42);
    hemi.position.set(0, 1, 0);
    this.root.add(hemi);

    const key = new THREE.DirectionalLight(0xfff1d6, 0.6);
    key.position.set(14, 32, 10);
    this.root.add(key);

    const rim = new THREE.DirectionalLight(0x6ac0ff, 0.38);
    rim.position.set(-16, 22, -8);
    this.root.add(rim);

    // Central warm point lamp (from skylight).
    const warm = new THREE.PointLight(0xffd389, 0.7, 80, 2.2);
    warm.position.set(0, StationLayout.CEILING_HEIGHT + 2, 0);
    this.root.add(warm);

    // Cool fill from atrium ring.
    const cool = new THREE.PointLight(0x9fd9ff, 0.55, 60, 2);
    cool.position.set(0, 10, 0);
    this.root.add(cool);

    // Hangar-side warm lamp.
    const hangarLamp = new THREE.PointLight(0xffb568, 0.6, 50, 2);
    hangarLamp.position.set(0, 6, StationLayout.HANGAR_CENTER_Z);
    this.root.add(hangarLamp);
  }

  private buildArchitecture(): void {
    const built = buildStationInterior();
    this.root.add(built.root);
    this._obstacles.push(...built.obstacles);
  }

  private buildAlcoves(): void {
    for (const kind of SHOP_ORDER) {
      const def = getShopDefinition(kind);
      const angle = StationLayout.SHOP_ANGLES[kind];

      // Build the alcove architecture at that angle.
      const alcove = buildAlcove(angle, def);
      this.root.add(alcove.root);
      this._obstacles.push(...alcove.obstacles);

      // Counter inside the alcove, facing the atrium.
      const counter = new StationCounter({
        kind,
        position: alcove.counterPosition,
        orientation: alcove.counterFacing
      });
      this.root.add(counter.object3D);
      this.counters.push(counter);
      this._obstacles.push({
        position: counter.object3D.position.clone(),
        radius: 2.4
      });

      // Vendor behind the counter (outward from center).
      const vendor = new StationVendor({
        preset: def.vendorPreset,
        position: counter.vendorAnchor.clone().setY(0),
        facing: alcove.counterFacing + Math.PI
      });
      this.root.add(vendor.object3D);
      this.vendors.push(vendor);
    }
  }

  private buildHangarZone(): void {
    const hangar = buildHangar();
    this.root.add(hangar.root);
    this._obstacles.push(...hangar.obstacles);
    this.exitPosition.copy(hangar.exitPosition);
    this.exitRadius = hangar.exitRadius;

    // Player ship parked on the central hangar platform.
    const playerShip = new PlayerShip({ selection: this.options.faction });
    playerShip.object3D.position.copy(hangar.playerShipPlatformPosition);
    playerShip.object3D.rotation.y = 0; // nose toward window/exit (+Z)
    this.entities.add(playerShip);

    // Two NPC ships parked in side docks.
    const npcDefs: Array<{
      factionId: 'guards' | 'miners';
      color: number;
      role: 'guard' | 'miner';
    }> = [
      {
        factionId: 'guards',
        color: FACTIONS.guards.defaultColor,
        role: 'guard'
      },
      {
        factionId: 'miners',
        color: FACTIONS.miners.defaultColor,
        role: 'miner'
      }
    ];

    hangar.npcDockPositions.forEach((pos, i) => {
      const spec = npcDefs[i % npcDefs.length]!;
      const ship = new NpcShip({
        factionId: spec.factionId,
        primaryColor: spec.color,
        role: spec.role,
        hullMax: 30,
        position: new THREE.Vector3(
          pos.x,
          StationLayout.HANGAR_PLATFORM_RAISE + 1.3 + StationLayout.FLOOR_Y,
          pos.z
        )
      });
      // Face toward the window (+Z).
      ship.object3D.rotation.y = 0;
      this.entities.add(ship);
    });
  }

  private spawnWanderers(): void {
    const faction = getFaction(this.options.faction.factionId);

    const presets: Array<import('../gameplay/station').WanderingNpcOptions> = [
      {
        suitColor: faction.palette.primary,
        suitDarkColor: faction.palette.primaryDark,
        accentColor: faction.palette.accent,
        skinColor: 0xd4a684,
        hairColor: 0x2a1608,
        kind: 'human-civ'
      },
      {
        suitColor: 0x2f4e7a,
        suitDarkColor: 0x17263e,
        accentColor: 0x9fd9ff,
        skinColor: 0xdbb08f,
        hairColor: 0x1a1a1a,
        kind: 'human-engineer'
      },
      {
        suitColor: 0x3a2a12,
        suitDarkColor: 0x1c1308,
        accentColor: 0xffb170,
        skinColor: 0xd4b98c,
        kind: 'alien-tall'
      },
      {
        suitColor: 0x4a3410,
        suitDarkColor: 0x2a1e08,
        accentColor: 0xffd389,
        skinColor: 0x8d7040,
        kind: 'robed'
      },
      {
        suitColor: 0x332b64,
        suitDarkColor: 0x1a163a,
        accentColor: 0xb48cff,
        skinColor: 0x7e5fbd,
        kind: 'alien-short'
      },
      {
        suitColor: 0x1d2432,
        suitDarkColor: 0x0f1420,
        accentColor: 0xff9a9a,
        skinColor: 0xb89478,
        hairColor: 0x3a2216,
        kind: 'heavy'
      },
      {
        suitColor: faction.palette.hull,
        suitDarkColor: faction.palette.hullDark,
        accentColor: faction.palette.primary,
        skinColor: 0xc09272,
        hairColor: 0x1a1410,
        kind: 'human-civ'
      }
    ];

    // Multiple path rings at different radii for natural circulation.
    const pathRings: THREE.Vector3[][] = [
      this.makePathRing(
        StationLayout.ATRIUM_RADIUS + 3,
        12,
        0.3,
        { minAngle: Math.PI * 0.1, maxAngle: Math.PI * 1.9 }
      ),
      this.makePathRing(
        StationLayout.INTERIOR_RADIUS * 0.45,
        14,
        0.4,
        { minAngle: Math.PI * 0.2, maxAngle: Math.PI * 1.8 }
      ),
      this.makePathRing(
        StationLayout.INTERIOR_RADIUS * 0.7,
        12,
        0.2,
        { minAngle: Math.PI * 0.2, maxAngle: Math.PI * 1.8 }
      )
    ];

    const totalNpcs = 7;
    for (let i = 0; i < totalNpcs; i++) {
      const preset = presets[i % presets.length]!;
      const ring = pathRings[i % pathRings.length]!;
      const npc = new WanderingNpc(ring, preset);
      if (ring.length > 0) {
        const startIdx = Math.floor(Math.random() * ring.length);
        npc.object3D.position.copy(ring[startIdx]!);
      }
      this.root.add(npc.object3D);
      this.wanderers.push(npc);
    }
  }

  private makePathRing(
    radius: number,
    points: number,
    jitter = 0,
    arcLimit?: { minAngle: number; maxAngle: number }
  ): THREE.Vector3[] {
    const out: THREE.Vector3[] = [];
    const startAngle = arcLimit?.minAngle ?? 0;
    const endAngle = arcLimit?.maxAngle ?? Math.PI * 2;
    for (let i = 0; i < points; i++) {
      const t = i / points;
      const angle = startAngle + (endAngle - startAngle) * t;
      const r = radius + (Math.random() - 0.5) * jitter * 2;
      out.push(new THREE.Vector3(Math.sin(angle) * r, 0, Math.cos(angle) * r));
    }
    return out;
  }

  private spawnAstronaut(): void {
    const faction = getFaction(this.options.faction.factionId);
    this.astronaut = new Astronaut({
      suit: faction.palette.hull,
      accent: faction.palette.primary,
      visor: 0x0e0e18,
      dark: 0x18181f,
      glove: 0x14141c,
      clipGlow: faction.palette.accent
    });
    this.astronaut.object3D.position.copy(this.spawnPosition);
    this.astronaut.object3D.rotation.y = this.spawnFacing;
    // The FPS camera sits inside the head — never render the body.
    this.astronaut.object3D.visible = false;
    this.entities.add(this.astronaut);
  }

  private registerUpdateSystem(): void {
    const system: System = {
      id: 'station-interior-idle',
      update: (dt: number) => {
        for (const c of this.counters) c.update(dt);
        for (const v of this.vendors) v.update(dt);
        for (const n of this.wanderers) n.update(dt);

        // Rotate the central emblem layers.
        const emblem = this.root.getObjectByName('station-emblem');
        const emblemInner = this.root.getObjectByName('station-emblem-inner');
        const emblemThin = this.root.getObjectByName('station-emblem-thin');
        if (emblem) emblem.rotation.y += dt * 0.35;
        if (emblemInner) emblemInner.rotation.y -= dt * 0.5;
        if (emblemThin) emblemThin.rotation.y += dt * 0.85;

        // Rotate the mission terminal holo near the base of the dais.
        const missionHolo = this.root.getObjectByName('mission-terminal-holo');
        if (missionHolo) {
          missionHolo.rotation.y += dt * 1.2;
          missionHolo.rotation.x += dt * 0.7;
        }

        // Vibejam exit portal idle animation.
        if (this.vibejamPortal) this.vibejamPortal.update(dt);

        // Wall containment for the astronaut.
        if (this.astronaut) {
          const pos = this.astronaut.object3D.position;
          const distSq = pos.x * pos.x + pos.z * pos.z;
          const maxR = StationLayout.INTERIOR_RADIUS - 1.2;
          if (distSq > maxR * maxR) {
            const dist = Math.sqrt(distSq);
            const factor = maxR / dist;
            pos.x *= factor;
            pos.z *= factor;
          }
        }
      }
    };
    this.systems.register(system);
  }
}
